// TikTok Pixel bridge. Like the Meta Pixel, the snippet is injected by
// DeferredAnalyticsScripts only after tracking consent, so every call here is a
// no-op until then — `ttq` simply does not exist yet.

import { createConversionEventId } from './conversionEventId'
import { whenPixelReady } from './whenPixelReady'

interface TikTokPixel {
  identify: (user: Record<string, string>) => void
  track: (
    event: string,
    properties?: Record<string, unknown>,
    options?: { event_id?: string }
  ) => void
  page: () => void
}

declare global {
  interface Window {
    ttq?: TikTokPixel
  }
}

/** Contact details of the account just created, for Advanced Matching. */
export interface SignupContact {
  email?: string | null
  phone?: string | null
}

// TikTok's half of the pixel ↔ Events API deduplication, see conversionEventId.
const eventIdSlot = createConversionEventId('arena_tt_event_id')

export const newTtEventId = eventIdSlot.mint
export const consumeTtEventId = eventIdSlot.consume

const toHex = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer), byte =>
    byte.toString(16).padStart(2, '0')
  ).join('')

const sha256 = async (value: string): Promise<string> =>
  toHex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)))

// TikTok matches on E.164. Phone numbers are stored with the country code but
// not always with the plus or without separators.
const toE164 = (phone: string): string => {
  const digits = phone.replaceAll(/\D/g, '')

  return digits ? `+${digits}` : ''
}

/**
 * Advanced Matching fields, SHA-256 hashed on the client so no raw personal
 * data leaves the browser. Empty when WebCrypto is unavailable (insecure
 * context): the conversion still goes out, only unmatched.
 */
const hashedIdentity = async (
  userId: string | null,
  contact: SignupContact
): Promise<Record<string, string>> => {
  const fields: [string, string][] = [
    ['email', contact.email?.trim().toLowerCase() ?? ''],
    ['phone_number', toE164(contact.phone ?? '')],
    ['external_id', userId?.trim() ?? '']
  ]

  try {
    const hashed = await Promise.all(
      fields
        .filter(([, value]) => value)
        .map(async ([key, value]) => [key, await sha256(value)] as const)
    )

    return Object.fromEntries(hashed)
  } catch {
    return {}
  }
}

/**
 * TikTok's standard conversion event for a completed signup.
 *
 * The backend reports the same conversion through the Events API with the id
 * minted for this signup, so TikTok collapses the two. A missing id still
 * reports the conversion, only without deduplication.
 */
export const trackTikTokCompleteRegistration = (
  userId: string | null,
  contact: SignupContact = {}
): void => {
  if (typeof window === 'undefined') {
    return
  }

  whenPixelReady(
    () => typeof window.ttq?.track === 'function',
    () => {
      // Consumed only once ttq exists, for the same reason as in metaPixel.
      const eventId = consumeTtEventId()

      void hashedIdentity(userId, contact).then(identity => {
        try {
          if (Object.keys(identity).length > 0) {
            window.ttq?.identify(identity)
          }
          window.ttq?.track(
            'CompleteRegistration',
            {},
            eventId ? { event_id: eventId } : undefined
          )
        } catch {
          // best-effort, mirrors pushDataLayerEvent
        }
      })
    }
  )
}
